// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Skeleton } from "../Skeleton";
import { fetchDataSources } from "../../api/awsLamba";
import type { DataSourceFormState, PostgresParameter } from "../../types/composedForecast";

import Select from "../ReusableComponents/Select";
import SourceHistoryOverride from "./SourceHistoryOverride";
interface PostgresSourceConfigProps {
  dataSource: DataSourceFormState;
  onUpdate: (updated: DataSourceFormState) => void;
  /** The view has a forecast: its History before the forecast decides the window, so this source's own lookback is hidden. */
  viewHasForecast?: boolean;
}

const PostgresSourceConfig: React.FC<PostgresSourceConfigProps> = ({
  dataSource,
  onUpdate,
  viewHasForecast = false,
}) => {
  const [parameters, setParameters] = useState<PostgresParameter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadParameters();
  }, []);

  const loadParameters = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchDataSources();
      setParameters(data || []);
    } catch (err) {
      console.error("Error loading postgres parameters:", err);
      setError("Failed to load data sources");
    } finally {
      setLoading(false);
    }
  };

  // Group parameters by datasource_name
  const grouped = parameters.reduce<Record<string, PostgresParameter[]>>((acc, param) => {
    const group = param.datasource_name || "Other";
    if (!acc[group]) acc[group] = [];
    acc[group].push(param);
    return acc;
  }, {});

  const handleParameterSelect = (parameterId: string) => {
    const id = parseInt(parameterId);
    const param = parameters.find((p) => p.parameter_id === id);
    if (param) {
      onUpdate({
        ...dataSource,
        parameterId: param.parameter_id,
        parameterName: param.parameter_name,
        datasourceName: param.datasource_name,
        sourceName: dataSource.sourceName || param.parameter_name,
      });
    }
  };

  if (loading) {
    return (
      // Field-shaped placeholder: the select lands in exactly this box once the options load
      <Skeleton className="h-eai-control-lg w-full" radius="md" />
    );
  }

  if (error) {
    return (
      <div className="py-2 font-inter text-eai-body-sm text-eai-danger-text">
        {error}{" "}
        <button onClick={loadParameters} className="underline">
          Retry
        </button>
      </div>
    );
  }

  // Grouped by data source, A–Z; search matches the parameter, its folder and the data source's name
  const parameterOptions = Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([groupName, params]) =>
      [...params]
        .sort((a, b) => a.parameter_name.localeCompare(b.parameter_name))
        .map((param) => ({
          value: String(param.parameter_id),
          label: param.parameter_name,
          description: param.folderLabel || undefined,
          group: groupName,
        }))
    );

  return (
    <div className="space-y-3">
      <div>
        <label className="eai-meta-label mb-2 block">
          Parameter
        </label>
        <Select
          searchable
          aria-label="Parameter"
          placeholder="Select a parameter..."
          value={dataSource.parameterId ? String(dataSource.parameterId) : ""}
          onChange={(e) => handleParameterSelect(e.target.value)}
          options={parameterOptions}
          containerClassName="w-full"
        />
        {/* The group a pick came from only shows while the list is open, so it stays named once closed */}
        {dataSource.datasourceName && (
          <p className="mt-1 font-inter text-eai-body-xs text-eai-ink-faint">From {dataSource.datasourceName}</p>
        )}
      </div>

      <div>
        <label className="eai-meta-label mb-2 block">
          Lookback Days
        </label>
        {viewHasForecast ? (
          <SourceHistoryOverride
            value={dataSource.sourceHistoryDays}
            onChange={(sourceHistoryDays) => onUpdate({ ...dataSource, sourceHistoryDays })}
          />
        ) : (
        /* Wraps as a whole line on a phone rather than orphaning the tail of the hint beside the field */
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <input
            type="number"
            value={dataSource.lookbackDays || 30}
            onChange={(e) =>
              onUpdate({ ...dataSource, lookbackDays: parseInt(e.target.value) || 30 })
            }
            min="1"
            max="365"
            className="eai-input w-24 shrink-0 sm:w-32"
          />
          <span className="font-inter text-eai-body-xs text-eai-ink-faint">
            days of history shown (the view has no forecast)
          </span>
        </div>
        )}
      </div>

    </div>
  );
};

export default PostgresSourceConfig;
